// UserNotificationModal.tsx
// Modal component for displaying user notifications from API (separate from Firebase notifications)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  Calendar, 
  Clock, 
  BarChart3, 
  RefreshCw, 
  AlertCircle,
  Loader2
} from 'lucide-react';
import logger, { Logger } from '../utils/logger';
import PopupComponent from './PopupComponent'; // 🆕 ADD PopupComponent import
import { 
  NotificationData, 
  getNotifications, 
  formatNotificationTime,
  getNotificationIcon,
  handleNotificationClick,
  getNotificationSummary,
  isNotificationClickable
} from '../services/userNotificationService';

// 🎨 USER NOTIFICATION MODAL PROPS INTERFACE
interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// 📱 NOTIFICATION ITEM COMPONENT
const NotificationItem: React.FC<{
  notification: NotificationData;
  onClick: (notification: NotificationData) => void;
}> = React.memo(({ notification, onClick }) => {
  logger.log('🔔 Rendering notification item:', notification._id);
  
  // Get appropriate icon and color for notification type
  const { icon, color } = getNotificationIcon(notification.notificationType);
  
  // Check if notification is clickable
  const isClickable = isNotificationClickable(notification);
  
  // Format time for display
  const timeAgo = formatNotificationTime(notification.createdAt);
  
  // Get summary text
  const summaryText = getNotificationSummary(notification);
  
  // Render appropriate icon
  const renderIcon = () => {
    switch (icon) {
      case 'Calendar':
        return <Calendar size={18} style={{ color }} />;
      case 'Clock':
        return <Clock size={18} style={{ color }} />;
      case 'BarChart3':
        return <BarChart3 size={18} style={{ color }} />;
      default:
        return <Bell size={18} style={{ color }} />;
    }
  };
  
  // Handle item click
  const handleClick = useCallback(() => {
    if (isClickable) {
      logger.log('🔔 Notification item clicked:', notification._id);
      onClick(notification);
    }
  }, [isClickable, notification, onClick]);
  
  return (
    <div
      className="notification-item"
      onClick={handleClick}
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e9ecef',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'background-color 0.2s ease',
        backgroundColor: 'transparent'
      }}
      onMouseEnter={(e) => {
        if (isClickable) {
          e.currentTarget.style.backgroundColor = '#f8f9fa';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Notification Icon */}
        <div style={{ flexShrink: 0, marginTop: '2px' }}>
          {renderIcon()}
        </div>
        
        {/* Notification Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title and Time */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '4px'
          }}>
            <h6 style={{ 
              margin: 0, 
              fontSize: '14px', 
              fontWeight: '600',
              color: '#212529',
              lineHeight: '1.3'
            }}>
              {notification.title}
            </h6>
            <span style={{ 
              fontSize: '12px', 
              color: '#6c757d',
              flexShrink: 0,
              marginLeft: '8px'
            }}>
              {timeAgo}
            </span>
          </div>
          
          {/* Body Text */}
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: '#495057',
            lineHeight: '1.4'
          }}>
            {summaryText}
          </p>
          
          {/* Lead Source Badge (if applicable) */}
          {notification.leadSource && (
            <div style={{ marginTop: '6px' }}>
              <span style={{
                fontSize: '11px',
                padding: '2px 6px',
                backgroundColor: '#e7f1ff',
                color: '#0d6efd',
                borderRadius: '4px',
                textTransform: 'capitalize'
              }}>
                {notification.leadSource}
              </span>
            </div>
          )}
          
          {/* Error Message (if any) */}
          {notification.error && (
            <div style={{ 
              marginTop: '6px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px' 
            }}>
              <AlertCircle size={14} color="#dc3545" />
              <span style={{ 
                fontSize: '12px', 
                color: '#dc3545',
                fontStyle: 'italic'
              }}>
                Error occurred
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// 🔔 MAIN USER NOTIFICATION MODAL COMPONENT
const UserNotificationModal: React.FC<NotificationModalProps> = ({ isOpen, onClose }) => {
  logger.log('🔔 UserNotificationModal render - isOpen:', isOpen);
  
  // Navigation hook
  const navigate = useNavigate();
  
  // Component state
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  
  // Constants
  const NOTIFICATIONS_PER_PAGE = 15;
  
  // 📡 FETCH USER NOTIFICATIONS FROM API
  const fetchNotifications = useCallback(async (page: number = 1, append: boolean = false) => {
    try {
      logger.log(`🔔 Fetching user notifications from API - Page: ${page}, Append: ${append}`);
      setLoading(true);
      setError(null);
      
      // Call user notification service API
      const response = await getNotifications(page, NOTIFICATIONS_PER_PAGE);
      
      logger.log('🔔 User notifications fetched successfully from API:', response);
      
      if (response.status && response.data && response.data.data) {
        const newNotifications = response.data.data;
        
        if (append) {
          // Append to existing notifications (Load More)
          setNotifications(prev => [...prev, ...newNotifications]);
        } else {
          // Replace notifications (Fresh load)
          setNotifications(newNotifications);
        }
        
        // Check if there's more data
        setHasMoreData(newNotifications.length === NOTIFICATIONS_PER_PAGE);
        setCurrentPage(page);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('❌ Error fetching notifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user notifications');
      
      // Don't clear existing notifications on error if appending
      if (!append) {
        setNotifications([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  // 🔄 REFRESH USER NOTIFICATIONS FROM API
  const refreshNotifications = useCallback(() => {
    logger.log('🔄 Refreshing user notifications from API');
    fetchNotifications(1, false);
  }, [fetchNotifications]);
  
  // 📄 LOAD MORE USER NOTIFICATIONS FROM API
  const loadMoreNotifications = useCallback(() => {
    if (!loading && hasMoreData) {
      logger.log('📄 Loading more user notifications from API');
      fetchNotifications(currentPage + 1, true);
    }
  }, [loading, hasMoreData, currentPage, fetchNotifications]);
  
  // 🖱️ HANDLE NOTIFICATION CLICK
  const handleNotificationItemClick = useCallback((notification: NotificationData) => {
    try {
      logger.log('🔔 Handling notification click:', notification);
      
      // Use user notification service to handle navigation
      handleNotificationClick(notification, navigate);
      
      // Close modal after navigation
      onClose();
    } catch (err) {
      console.error('❌ Error handling notification click:', err);
      Logger.log('Error in notification click handler:', err);
    }
  }, [navigate, onClose]);
  
  // 🚀 INITIAL LOAD EFFECT
  useEffect(() => {
    if (isOpen) {
      logger.log('🔔 User notification modal opened, fetching notifications from API');
      fetchNotifications(1, false);
    }
  }, [isOpen, fetchNotifications]);
  
  // 🚫 EARLY RETURN IF MODAL IS CLOSED
  if (!isOpen) {
    return null;
  }

  // 🎨 HEADER ACTIONS (Refresh Button)
  const headerActions = (
    <button
      onClick={refreshNotifications}
      disabled={loading}
      style={{
        background: 'transparent',
        border: '1px solid #e9ecef',
        borderRadius: '4px',
        padding: '6px',
        cursor: loading ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '8px'
      }}
      title="Refresh user notifications"
    >
      <RefreshCw 
        size={16} 
        color="#6c757d"
        className={loading ? 'animate-spin' : ''}
      />
    </button>
  );

  return (
    <PopupComponent
      isOpen={isOpen}
      onClose={onClose}
      title="User Notifications"
      size="md"
      headerActions={headerActions}
    >
      {/* 🔄 NOTIFICATION CONTENT */}
      <div style={{ minHeight: '300px', maxHeight: '60vh', overflowY: 'auto' }}>
        {/* Loading State */}
        {loading && notifications.length === 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '40px 20px',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <Loader2 size={24} className="animate-spin" color="#6c757d" />
            <span style={{ color: '#6c757d', fontSize: '14px' }}>Loading user notifications...</span>
          </div>
        )}
        
        {/* Error State */}
        {error && notifications.length === 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '40px 20px',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <AlertCircle size={24} color="#dc3545" />
            <span style={{ color: '#dc3545', fontSize: '14px', textAlign: 'center' }}>
              {error}
            </span>
            <button
              onClick={refreshNotifications}
              style={{
                backgroundColor: '#0d6efd',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Retry
            </button>
          </div>
        )}
        
        {/* Empty State */}
        {!loading && !error && notifications.length === 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '40px 20px',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <Bell size={24} color="#6c757d" />
            <span style={{ color: '#6c757d', fontSize: '14px' }}>No user notifications found</span>
          </div>
        )}
        
        {/* Notifications List */}
        {notifications.length > 0 && (
          <>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                onClick={handleNotificationItemClick}
              />
            ))}
            
            {/* Load More Section */}
            {hasMoreData && (
              <div style={{ 
                padding: '16px 20px', 
                textAlign: 'center',
                borderTop: '1px solid #f1f3f4'
              }}>
                <button
                  onClick={loadMoreNotifications}
                  disabled={loading}
                  style={{
                    backgroundColor: loading ? '#f8f9fa' : '#0d6efd',
                    color: loading ? '#6c757d' : '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    margin: '0 auto'
                  }}
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
            
            {/* No More Data Message */}
            {!hasMoreData && notifications.length >= NOTIFICATIONS_PER_PAGE && (
              <div style={{ 
                padding: '16px 20px', 
                textAlign: 'center',
                borderTop: '1px solid #f1f3f4'
              }}>
                <span style={{ 
                  color: '#6c757d', 
                  fontSize: '13px',
                  fontStyle: 'italic'
                }}>
                  No more user notifications to load
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </PopupComponent>
)};


export default React.memo(UserNotificationModal);